/**
 * This file contains the configuration for the two-factor auth settings state machine.
 */

/**
 * The states which map to the content being displayed on the page.
 */
export const States = {
  /**
   * Used to indicate that the initial state is not yet known.
   * @internal
   */
  LOADING: 'loading',

  /** The user does not have two-factor auth enabled. */
  OFF: 'off',

  /** The user has two-factor auth enabled with an authenticator app. */
  AUTH_APP_STATUS: 'authAppStatus',

  /** The user has two-factor auth enabled with SMS. */
  SMS_STATUS: 'smsStatus',

  /** The user can pick a method to setup two-factor auth. */
  SELECT_AUTH_METHOD: 'selectAuthMethod',

  /** The user is setting up two-factor auth with an authenticator app. */
  AUTH_APP_SETUP: 'authAppSetup',

  /** The user is setting up two-factor auth with SMS. */
  SMS_SETUP: 'smsSetup',
} as const;
export type States = (typeof States)[keyof typeof States];

/**
 * The events which can be triggered by the user to transition to the next state.
 */
export const StateEvent = {
  /** The user has clicked the "Setup Two-Factor Auth" button. */
  START_SETUP: 'startSetup',

  /** The user has selected the authenticator app method from the list of methods. */
  SELECT_AUTH_APP_METHOD: 'selectAuthAppMethod',

  /** The user has selected the SMS method from the list of methods. */
  SELECT_SMS_METHOD: 'selectSmsMethod',

  /** The user has selected to not use two-factor auth. */
  SELECT_NO_METHOD: 'selectNoMethod',

  /** The user has finished setting up two-factor auth. */
  FINISH_SETUP: 'finishSetup',

  /** The user has turned off a two-factor auth method. */
  TURN_OFF_METHOD: 'turnOffMethod',

  /** The user has decided to pick a different method for two-factor auth. */
  PICK_DIFFERENT_METHOD: 'pickDifferentMethod',
} as const;
export type StateEvent = (typeof StateEvent)[keyof typeof StateEvent];

/**
 * This maps how to transition between states based on the event.
 */
export const Transitions: Record<
  States,
  Partial<Record<StateEvent, States>>
> = {
  [States.LOADING]: {},
  [States.OFF]: {
    [StateEvent.START_SETUP]: States.SELECT_AUTH_METHOD,
  },
  [States.AUTH_APP_STATUS]: {
    [StateEvent.TURN_OFF_METHOD]: States.OFF,
    [StateEvent.START_SETUP]: States.SELECT_AUTH_METHOD,
  },
  [States.SMS_STATUS]: {
    [StateEvent.TURN_OFF_METHOD]: States.OFF,
    [StateEvent.START_SETUP]: States.SELECT_AUTH_METHOD,
  },
  [States.SELECT_AUTH_METHOD]: {
    [StateEvent.SELECT_AUTH_APP_METHOD]: States.AUTH_APP_SETUP,
    [StateEvent.SELECT_SMS_METHOD]: States.SMS_SETUP,
    [StateEvent.SELECT_NO_METHOD]: States.OFF,
  },
  [States.AUTH_APP_SETUP]: {
    [StateEvent.PICK_DIFFERENT_METHOD]: States.SELECT_AUTH_METHOD,
    [StateEvent.FINISH_SETUP]: States.AUTH_APP_STATUS,
  },
  [States.SMS_SETUP]: {
    [StateEvent.PICK_DIFFERENT_METHOD]: States.SELECT_AUTH_METHOD,
    [StateEvent.FINISH_SETUP]: States.SMS_STATUS,
  },
} as const;
